import { useFormik } from "formik";
import * as Yup from "yup";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { useState, useEffect, useRef } from "react";
import ComplaintService from "../../services/complaint/ComplaintService";
import { complaintmodel2 } from "../../models/complaints/complaint-model";
import { InputText } from "primereact/inputtext";
import { ImageTemplateDemo } from "../images/file-upload-template";
import { Image } from "primereact/image";

interface UpdateServiceComplaintProps {
  isUpdate: boolean;
  handleOnSuccess: (updatedComplaint: complaintmodel2) => void;
  updateComplaintInfo: complaintmodel2;
}

const UpdateCustServiceComplaintDataTable: React.FC<UpdateServiceComplaintProps> = ({ 
  isUpdate, 
  handleOnSuccess, 
  updateComplaintInfo,
}) => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(false);
  const [loginUser, setLoginUser] = useState<any>();
   const [updateImage,setUpdateImage] = useState<any>();

  useEffect(() => {
    const LoginDetails = localStorage.getItem('user');
    if (LoginDetails != null) {
      setLoginUser(JSON.parse(LoginDetails));
    }

    formik.setValues({
      id: updateComplaintInfo.id || 0,
      customerId: updateComplaintInfo.customerId || 0,
      description: updateComplaintInfo.description || "",
      image: updateComplaintInfo.image || null,
      remarks: updateComplaintInfo.remarks || "",
      customerSupportAction: updateComplaintInfo.customerSupportAction || "",
      severity: updateComplaintInfo.severity || "Medium",
      createdBy: updateComplaintInfo.createdBy || 0,
      createdDate: updateComplaintInfo.createdDate || "",
      modifiedBy: updateComplaintInfo.modifiedBy || 0,
      modifiedDate: updateComplaintInfo.modifiedDate || "",
      customerName: updateComplaintInfo.customerName || "",
      createdByName: updateComplaintInfo.createdByName || "",
    });
  }, [updateComplaintInfo]);

  const formik = useFormik({
    initialValues: {
      id: updateComplaintInfo.id || 0,
      customerId: updateComplaintInfo.customerId || 0,
      description: updateComplaintInfo.description || "",
      image: updateComplaintInfo.image || null,
      remarks: updateComplaintInfo.remarks || "",
      customerSupportAction: updateComplaintInfo.customerSupportAction || "",
      severity: updateComplaintInfo.severity || "Medium",
      createdBy: updateComplaintInfo.createdBy || 0,
      createdDate: updateComplaintInfo.createdDate || "",
      modifiedBy: updateComplaintInfo.modifiedBy || 0,
      modifiedDate: updateComplaintInfo.modifiedDate || "",
      customerName: updateComplaintInfo.customerName || "",
      createdByName: updateComplaintInfo.createdByName || "",
    },
    validationSchema: Yup.object({
      description: Yup.string().required("Description is required"),
      severity: Yup.string().required("Severity is required"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const complaintData = {
          id: values.id,
          customerId: values.customerId,
          description: values.description,
          image: updateImage,
          remarks: values.remarks,
          customerSupportAction: values.customerSupportAction,
          severity: values.severity,
          modifiedBy: loginUser?.userID || 0,
          modifiedDate: new Date().toISOString(),
          modifiedByName: loginUser?.name || "System",
          containerType : updateComplaintInfo.containerType
        };

        const response = await ComplaintService.UpdateCustServiceComplaintInfo(complaintData);
        for (let index = 0; index < response.image.length; index++) {
          updateComplaintInfo.image.push(response.image[index])
        }
        complaintData.image = updateComplaintInfo.image;
        if (response) {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Service complaint updated successfully',
            life: 3000
          });
          
          handleOnSuccess({
            ...updateComplaintInfo,
            ...complaintData,
            customerName: values.customerName,
            createdByName: values.createdByName,
          });
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update service complaint',
            life: 3000
          });
        }
      } catch (error) {
        console.error("Error updating service complaint:", error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update service complaint',
          life: 3000
        });
      } finally {
        setLoading(false);
      }
    }
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const inputFieldClasses = "w-full p-2 border-round border-1 border-300";
  const textareaClasses = `${inputFieldClasses} resize-none`;
  const readOnlyFieldClasses = `${inputFieldClasses} bg-gray-100`;

  const handelfileUpload = (file: any) => {
    setUpdateImage(file);
  };

  const handelfileSizeExceeded = (message: string) => {
    toast.current?.show({
      severity: 'info',
      summary: 'Info',
      detail: message,
      life: 3000
    });
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="col-12 flex align-items-stretch flex-wrap">
        <div className="flex flex-1 bg-white align-items-center justify-content-center">
          <form onSubmit={formik.handleSubmit} className="w-full">
            <h5 className="border-round mb-4 p-3 order-top-color p-2 panel-header-text text-center">
              Update Service Complaint
            </h5>
            <div className="p-fluid formgrid grid">
              <div className="col-12">
                <div className="grid">
                  {/* Read-only fields */}
                  <div className="col-12 md:col-4 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="createdDate" className="block mb-2 font-bold">
                        Complaint Date
                      </label>
                      <InputText
                        id="createdDate"
                        name="createdDate"
                        value={formatDate(formik.values.createdDate)}
                        className={readOnlyFieldClasses}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-4 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="customerName" className="block mb-2 font-bold">
                        Customer Name
                      </label>
                      <InputText
                        id="customerName"
                        name="customerName"
                        value={formik.values.customerName}
                        className={readOnlyFieldClasses}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="col-12 md:col-4 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="createdByName" className="block mb-2 font-bold">
                        Created By
                      </label>
                      <InputText
                        id="createdByName"
                        name="createdByName"
                        value={formik.values.createdByName}
                        readOnly
                      />
                    </div>
                  </div>

                  {/* Editable fields */}
                  <div className="col-12 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="description" className="block mb-2 font-bold required-label">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <InputTextarea
                        id="description"
                        name="description"
                        rows={3}
                        value={formik.values.description}
                        onChange={(e) => {
                          const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                           formik.setFieldValue('description', cleanedValue);
                          }}
                        className={classNames(textareaClasses, { 
                          'p-invalid': formik.errors.description && formik.touched.description 
                        })}
                      />
                      {formik.errors.description && formik.touched.description && (
                        <small className="p-error block mt-1">{formik.errors.description as string}</small>
                      )}
                    </div>
                  </div>
                  <ImageTemplateDemo onFileUpload={handelfileUpload} handelFileSize={handelfileSizeExceeded}/>
                                      {formik.values.image?.map((x, index: number) => {
                                                          const fileUrl = x.fileDetails?.url;
                                                          const fileType = x.fileDetails?.type?.toLowerCase() || '';
                                                          const fileName = x.fileDetails?.name || `File-${index + 1}`;
                                      
                                                          const isImage = fileType.startsWith('image');
                                                          const isPDF = fileType === 'application/pdf';
                                                          const isVideo = fileType.startsWith('video');
                                                          const isExcel = fileType.includes('spreadsheet') || fileType.includes('excel');
                                                          const isWord = fileType.includes('word');
                                                          const isZip = fileType.includes('zip') || fileType.includes('compressed');
                                      
                                                          const iconClass = isExcel
                                                            ? 'pi pi-file-excel'
                                                            : isWord
                                                            ? 'pi pi-file-word'
                                                            : isZip
                                                            ? 'pi pi-file-archive'
                                                            : 'pi pi-file';
                                      
                                                          return (
                                                            <div key={index} className="col-12 md:col-6 lg:col-4 mb-3 px-3">
                                                              <div className="card justify-content-center text-center p-3">
                                      
                                                                {/* ✅ Image Preview */}
                                                                {isImage && (
                                                                  <>
                                                                    <Image src={fileUrl} alt={fileName} width="250" preview />
                                                                  </>
                                                                )}
                                      
                                                                {/* ✅ Video Preview */}
                                                                {isVideo && (
                                                                  <>
                                                                    <video controls width="100%" height="240">
                                                                      <source src={fileUrl} type="video/mp4" />
                                                                    </video>
                                                                  </>
                                                                )}
                                      
                                                                {/* ✅ PDF Preview */}
                                                                {isPDF && (
                                                                  <>
                                                                    <embed
                                                                      src={fileUrl}
                                                                      type="application/pdf"
                                                                      width="100%"
                                                                      height="300px"
                                                                      style={{ borderRadius: '6px', border: '1px solid #ccc' }}
                                                                    />
                                                                    <div className="mt-2">{fileName}</div>
                                                                    <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="p-button p-button-sm p-button-text mt-2">
                                                                      Open PDF
                                                                    </a>
                                                                  </>
                                                                )}
                                      
                                                                {/* ✅ Other File Types */}
                                                                {!isImage && !isPDF && !isVideo && (
                                                                  <>
                                                                    <i className={iconClass} style={{ fontSize: '3rem', color: '#6b7280' }}></i>
                                                                    <div className="mt-2">{fileName}</div>
                                                                    <a href={fileUrl} download className="p-button p-button-sm p-button-outlined mt-2">
                                                                      Download
                                                                    </a>
                                                                  </>
                                                                )}
                                                              </div>
                                                            </div>
                                                          );
                                                        })}
                  <div className="col-12 mt-4 px-3 flex justify-content-end">
                    <Button 
                      type="submit" 
                      label="Update Service Complaint" 
                      className="p-button-primary w-full md:w-auto" 
                      disabled={loading}
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UpdateCustServiceComplaintDataTable;